import { ref } from 'vue';
import logo from '@/assets/orion-logo-white-bg.png';

export function useTicketImage() {
  const loading = ref(false);

  const generateTicketImage = async (ticket) => {
    loading.value = true;
    try {
      // Create high-DPI canvas
      const canvas = document.createElement('canvas');
      const scale = 2; // Increase for higher quality
      canvas.width = 500 * scale;
      canvas.height = 500 * scale;
      const ctx = canvas.getContext('2d');
      
      // Scale everything up for higher DPI
      ctx.scale(scale, scale);
      
      // Enable text anti-aliasing
      ctx.textRendering = 'optimizeLegibility';
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';

      // Set background
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Load and draw logo
      const logoImg = new Image();
      await new Promise((resolve) => {
        logoImg.onload = resolve;
        logoImg.src = logo; // Using the imported logo URL
      });

      // Draw logo at top center (scaled down)
      const logoWidth = 150;
      const logoHeight = (logoImg.height / logoImg.width) * logoWidth;
      // Since we scaled the canvas but want to work in 500x500 space, use 250 as center point
      ctx.drawImage(logoImg, (500 - logoWidth) / 2, 30, logoWidth, logoHeight);

      // Set text styles with system fonts for better rendering
      ctx.fillStyle = '#000000';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';

      // Draw ticket number
      ctx.font = '20px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
      ctx.fillText(`Ticket #${ticket.id}`, 250, logoHeight + 80);

      // Draw subject
      ctx.font = 'bold 26px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
      ctx.fillText(ticket.subject, 250, logoHeight + 120);

      // Draw employee info
      ctx.font = '20px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
      ctx.fillText(`${ticket.employee.name}`, 250, logoHeight + 150);

      // Get latest status from posts
      const latestPost = ticket.posts[ticket.posts.length - 1];
      const statusText = `Status: ${latestPost.type === 'initial' ? 'Created' : 
                          latestPost.type === 'reconcile' ? 'Reconciled' : 
                          latestPost.type === 'close' ? 'Closed' : 'Updated'}`;

      // Draw status
      ctx.font = 'bold 20px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
      ctx.fillText(statusText, 250, logoHeight + 190);

      // Draw date
      const date = new Date(latestPost.timestamp).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
      ctx.font = '20px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
      ctx.fillText(date, 250, logoHeight + 220);

      // Draw footer
      ctx.font = '18px bold -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
      ctx.fillText('Open my.imab.app to see ticket details', 250, 460);

      // Convert to blob and download
      const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/png'));
      const url = URL.createObjectURL(blob);
      
      // Create download link
      const a = document.createElement('a');
      a.href = url;
      a.download = `ticket-${ticket.id}.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      return url;
    } catch (error) {
      console.error('Error generating ticket image:', error);
      throw error;
    } finally {
      loading.value = false;
    }
  };

  return {
    generateTicketImage,
    loading
  };
}
